import React, { useRef } from 'react';
import s from '../../styles/AdminDocumentMap.module.css';
import DocumentTypeTable from '../admin/DocumentTypeTable';
import FieldTypeTable from '../admin/FieldTypeTable';
import SubcategoryTable from '../admin/SubcategoryTable';
import AddDocumentTypeRow, { AddDocumentTypeRowHandle } from '../admin/AddDocumentTypeRow';
import AddFieldTypeRow, { AddFieldTypeRowHandle } from '../admin/AddFieldTypeRow';
import AddSubcategoryRow, { AddSubcategoryRowHandle } from '../admin/AddSubcategoryRow';
import AddCategoryRow, { AddCategoryRowHandle } from '../admin/AddCategoryRow';
import { useAdmin } from '../../contexts/AdminContext';
import { LevelType, TabCategory, TabItem, Subcategory, DocumentMapCategory } from '../../types/AdminTypes';
// ── Main component ────────────────────────────────────────────────────────

const AdminDocumentMap: React.FC = () => {
  const {
    documentMapLoading,
    documentMapError,
    level,
    setLevel,
    filteredCats,
    selectedCat,
    selectedCatId,
    selectedSubcat,
    selectCategory,
    drillIntoSubcat,
    exitSubcat,
    catTab,
    setCatTab,
    subcatTab,
    setSubcatTab,
    catEdit,
    addCategory,
    renameCategory,
    deleteCategory,
    addSubcategory,
    renameSubcategory,
    deleteSubcategory,
    addDocumentType,
    updateDocumentType,
    deleteDocumentType,
    addFieldType,
    updateFieldType,
    deleteFieldType,
    templatesMap,
    uploadTemplate,
    downloadAdminTemplate,
    deleteTemplate,
  } = useAdmin();

  const addSubcatRef = useRef<AddSubcategoryRowHandle>(null);
  const addDocTypeRef = useRef<AddDocumentTypeRowHandle>(null);
  const addFieldTypeRef = useRef<AddFieldTypeRowHandle>(null);
  const addCategoryRef = useRef<AddCategoryRowHandle>(null);

  // ── Render ─────────────────────────────────────────────────────────────
  if (documentMapLoading) return <div className={s.loadingState}>Loading document map…</div>;
  if (documentMapError) return <div className={s.errorState}>{documentMapError}</div>;

  const renderCatRightPanel = () => {
    if (!selectedCat) return <div className={s.emptyRight}>Select a category</div>;

    const subcats = (selectedCat.subcategories as Subcategory[] | undefined) ?? [];
    const catDocTypes = selectedCat.documentTypes ?? [];
    const catFields = selectedCat.fieldTypes ?? [];
    
    return (
      <>
        <div className={s.rightPanelHeader}>
          <div className={s.breadcrumb}>
            {selectedSubcat ? (
              <>
                <button className={s.breadcrumbLink} onClick={exitSubcat}>{selectedCat.name}</button>
                <span className={s.breadcrumbSep}>›</span>
                <span className={s.breadcrumbCurrent}>{selectedSubcat.name}</span>
              </>
            ) : (
              <span className={s.breadcrumbCurrent}>{selectedCat.name}</span>
            )}
          </div>
        </div>

        {selectedSubcat ? (
          // ── Subcategory drill-in ──────────────────────────────────────
          <>
            <div className={s.tabs}>
              {(['documentTypes', 'fields'] as TabItem[]).map(t => (
                <button key={t} className={`${s.tab} ${subcatTab === t ? s.tabActive : ''}`} onClick={() => setSubcatTab(t)}>
                  {t === 'documentTypes' ? 'Document Types' : 'Field Types'}
                </button>
              ))}
              <button
                className={`${s.btnSm} ${s.btnPrimary} ${s.btnTable}`}
                style={{ marginLeft: 'auto' }}
                onClick={() => subcatTab === 'documentTypes' ? addDocTypeRef.current?.open() : addFieldTypeRef.current?.open()}
              >
                + Add
              </button>
            </div>
            <div className={s.tabContent}>
              {subcatTab === 'documentTypes' && (
                <>
                  <DocumentTypeTable
                    rows={selectedSubcat.documentTypes}
                    onSave={row => updateDocumentType(row, selectedCat.id, selectedSubcat.id)}
                    onDelete={id => deleteDocumentType(id, selectedCat.id, selectedSubcat.id)}
                    categoryId={selectedCat.id}
                    subcategoryId={selectedSubcat.id}
                    templatesMap={templatesMap}
                    onUploadTemplate={uploadTemplate}
                    onDownloadTemplate={downloadAdminTemplate}
                    onDeleteTemplate={deleteTemplate}
                  />
                  <AddDocumentTypeRow ref={addDocTypeRef} onAdd={(name, recurrence, multi, viewingRole, edittingRole) => addDocumentType(name, recurrence, multi, viewingRole, edittingRole, selectedCat.id, selectedSubcat.id)} />
                </>
              )}
              {subcatTab === 'fields' && (
                <>
                  <FieldTypeTable
                    rows={selectedSubcat.fieldTypes}
                    onSave={row => updateFieldType(row, selectedCat.id, selectedSubcat.id)}
                    onDelete={id => deleteFieldType(id, selectedCat.id, selectedSubcat.id)}
                  />
                  <AddFieldTypeRow ref={addFieldTypeRef} onAdd={(name, value_type, required, viewingRole, edittingRole) => addFieldType(name, value_type, required, viewingRole, edittingRole, selectedCat.id, selectedSubcat.id)} />
                </>
              )}
            </div>
          </>
        ) : (
          // ── Category detail ───────────────────────────────────────────
          <>
            <div className={s.tabs}>
              {(['subcategories', 'documentTypes', 'fields'] as TabCategory[]).map(t => (
                <button key={t} className={`${s.tab} ${catTab === t ? s.tabActive : ''}`} onClick={() => setCatTab(t)}>
                  {t === 'subcategories' ? 'Subcategories' : t === 'documentTypes' ? 'Document Types' : 'Field Types'}
                </button>
              ))}
              <button
                className={`${s.btnSm} ${s.btnPrimary} ${s.btnTable}`}
                style={{ marginLeft: 'auto' }}
                onClick={() => {
                  if (catTab === 'subcategories') addSubcatRef.current?.open();
                  else if (catTab === 'documentTypes') addDocTypeRef.current?.open();
                  else addFieldTypeRef.current?.open();
                }}
              >
                + Add
              </button>
            </div>
            <div className={s.tabContent}>
              {catTab === 'subcategories' && (
                <>
                  <SubcategoryTable
                    rows={subcats}
                    onSave={renameSubcategory}
                    onDelete={id => deleteSubcategory(id)}
                    onDrillIn={row => drillIntoSubcat(row as Subcategory)}
                  />
                  <AddSubcategoryRow ref={addSubcatRef} onAdd={addSubcategory} />
                </>
              )}
              {catTab === 'documentTypes' && (
                <>
                  <DocumentTypeTable
                    rows={catDocTypes}
                    onSave={row => updateDocumentType(row, selectedCat.id)}
                    onDelete={id => deleteDocumentType(id, selectedCat.id)}
                    categoryId={selectedCat.id}
                    templatesMap={templatesMap}
                    onUploadTemplate={uploadTemplate}
                    onDownloadTemplate={downloadAdminTemplate}
                    onDeleteTemplate={deleteTemplate}
                  />
                  <AddDocumentTypeRow ref={addDocTypeRef} onAdd={(name, recurrence, multi, viewingRole, edittingRole) => addDocumentType(name, recurrence, multi, viewingRole, edittingRole, selectedCat.id)} />
                </>
              )}
              {catTab === 'fields' && (
                <>
                  <FieldTypeTable
                    rows={catFields}
                    onDelete={id => deleteFieldType(id, selectedCat.id)}
                    onSave={row => updateFieldType(row, selectedCat.id)}
                  />
                  <AddFieldTypeRow ref={addFieldTypeRef} onAdd={(name, value_type, required, viewingRole, edittingRole) => addFieldType(name, value_type, required, viewingRole, edittingRole, selectedCat.id)} />
                </>
              )}
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <div className={s.page}>
      {/* Level toggle */}
      <div className={s.toggleBar}>
        <button
          className={`${s.toggleBtn} ${level === 'employee' ? s.toggleBtnActive : ''}`}
          onClick={() => setLevel('employee')}
        >
          Employee Documents
        </button>
        <button
          className={`${s.toggleBtn} ${level === 'company' ? s.toggleBtnActive : ''}`}
          onClick={() => setLevel('company')}
        >
          Company Documents
        </button>
      </div>

      {/* Split pane */}
      <div className={s.splitPane}>
        {/* Left — category list */}
        <div className={s.leftPanel}>
          <div className={s.leftPanelHeader}>Categories</div>
          <div className={s.categoryList}>
            {filteredCats.length === 0 && <p style={{ padding: '12px 16px', color: '#94a3b8', fontSize: 13 }}>No categories yet.</p>}
            {filteredCats.map(cat => (
              <div
                key={cat.id}
                className={`${s.categoryRow} ${selectedCatId === cat.id ? s.categoryRowActive : ''}`}
                onClick={() => selectCategory(cat.id)}
              >
                {catEdit.editingId === cat.id ? (
                  <span className={s.inlineEdit} onClick={e => e.stopPropagation()}>
                    <input
                      className={s.inlineInput}
                      value={catEdit.editValue}
                      onChange={e => catEdit.setEditValue(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') renameCategory(cat.id); if (e.key === 'Escape') catEdit.cancel(); }}
                      autoFocus
                      style={{ width: 120 }}
                    />
                  </span>
                ) : (
                  <span className={s.categoryRowName}>{cat.name}</span>
                )}
                <span className={s.categoryRowActions}>
                  <button className={s.iconBtn} title="Rename" onClick={e => { e.stopPropagation(); catEdit.start(cat.id, cat.name ?? ''); }}>✏️</button>
                  <button className={`${s.iconBtn} ${s.iconBtnDanger}`} title="Delete" onClick={e => { e.stopPropagation(); deleteCategory(cat.id); }}>🗑️</button>
                </span>
              </div>
            ))}
          </div>
          <div className={s.leftPanelFooter}>
            <button className={`${s.btnSm} ${s.btnPrimary}`} style={{ display: 'block', margin: '0 auto' }} onClick={() => addCategoryRef.current?.open()}>+ Add Category</button>
          </div>
          <AddCategoryRow ref={addCategoryRef} onAdd={addCategory} />
        </div>

        {/* Right — detail panel */}
        <div className={s.rightPanel}>
          {renderCatRightPanel()}
        </div>
      </div>
    </div>
  );
};

export default AdminDocumentMap;
